import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SlidersHorizontal, Map, Layers } from "lucide-react";
import NavTop from "@/components/nav/nav_top";
import SwipeDeck from "@/components/swipe/swipe_deck";
import RenterSwipeDeck from "@/components/swipe/renter_swipe_deck";
import FilterSheet from "@/components/swipe/filter_sheet";
import { useAuth, userMode } from "@/context/AuthContext";

// The filters pill + deck/map toggle. Rendered twice: floating over the
// full-bleed card on mobile, inline in the desktop header row.
function DeckControls({ activeCount, onFilters, onMap, floating = false }) {
  const pill = floating
    ? "border-night-line bg-night-surface/90 backdrop-blur"
    : "border-night-line bg-night-surface";
  const navigateLabel = floating ? "" : "lg:px-3.5 lg:text-sm";
  return (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={onFilters}
        className={`relative inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-[13px] font-medium text-night-text shadow-ambient transition-shadow hover:shadow-ambient lg:gap-2 lg:px-4 lg:text-sm ${pill}`}
      >
        <SlidersHorizontal size={15} /> Filters
        {activeCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-brand-primary text-[10px] font-semibold text-white">
            {activeCount}
          </span>
        )}
      </button>
      <div className={`flex overflow-hidden rounded-full border shadow-ambient ${pill}`}>
        <span
          aria-label="Deck view"
          className={`flex items-center gap-1.5 bg-brand-primary px-3 py-2 text-[13px] font-medium text-white ${navigateLabel}`}
        >
          <Layers size={14} /> <span className="hidden lg:inline">Deck</span>
        </span>
        <button
          type="button"
          aria-label="Map view"
          onClick={onMap}
          className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium text-night-sub transition hover:text-night-text ${navigateLabel}`}
        >
          <Map size={14} /> <span className="hidden lg:inline">Map</span>
        </button>
      </div>
    </div>
  );
}

export default function SwipePage() {
  const [filters, setFilters] = useState({});
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  // FIX 3 — in LISTER mode the primary /swipe deck shows RENTER cards (people
  // looking for a place); in RENTER mode it shows listings exactly as before.
  const listerMode = userMode(user) === "lister";

  const activeCount = Object.values(filters).filter((v) => v && v !== "any").length;

  if (listerMode) {
    // Lister mode: SAME Tinder-style full bleed as the renter deck below —
    // on mobile the renter card fills everything between the top nav and the
    // bottom tab bar, edge to edge; the listing selector and notices float
    // over the photo. Desktop keeps the header row + large centered card.
    return (
      <div className="flex h-dvh flex-col bg-white lg:h-auto lg:min-h-screen">
        <NavTop />
        <main className="relative flex w-full flex-1 flex-col overflow-hidden pb-[calc(66px+env(safe-area-inset-bottom,0px))] md:pb-0 lg:mx-auto lg:max-w-[1240px] lg:overflow-visible lg:px-10 lg:pb-10 lg:pt-6">
          {/* Desktop-only header row */}
          <div className="hidden lg:mb-6 lg:block">
            <h1 className="truncate text-[22px] font-semibold tracking-tight text-night-text">Discover renters</h1>
            <p className="text-sm text-night-sub">Right to like, left to pass</p>
          </div>
          <RenterSwipeDeck fullBleed />
        </main>
      </div>
    );
  }

  // Renter mode: TINDER-STYLE FULL-BLEED. On mobile the card IS the page —
  // it fills everything between the top nav and the bottom tab bar, edge to
  // edge, no white frame. The deck controls float over the photo. Desktop
  // keeps the classic header row + large centered card.
  return (
    <div className="flex h-dvh flex-col bg-white lg:h-auto lg:min-h-screen">
      <NavTop />

      <main className="relative flex w-full flex-1 flex-col overflow-hidden pb-[calc(66px+env(safe-area-inset-bottom,0px))] md:pb-0 lg:mx-auto lg:max-w-[1240px] lg:overflow-visible lg:px-10 lg:pb-10 lg:pt-6">
        {/* Desktop-only header row */}
        <div className="hidden items-center justify-between gap-2 lg:mb-6 lg:flex">
          <div className="min-w-0">
            <h1 className="truncate text-[22px] font-semibold tracking-tight text-night-text">Discover homes</h1>
            <p className="text-sm text-night-sub">Right to like, left to pass</p>
          </div>
          <DeckControls
            activeCount={activeCount}
            onFilters={() => setSheetOpen(true)}
            onMap={() => navigate("/map")}
          />
        </div>

        {/* Mobile-only floating controls, below the card's photo dots */}
        <div className="absolute right-3 top-14 z-30 lg:hidden">
          <DeckControls
            floating
            activeCount={activeCount}
            onFilters={() => setSheetOpen(true)}
            onMap={() => navigate("/map")}
          />
        </div>

        {/* pauseKeys: keyboard swiping (arrows and U for undo, wired inside
            the deck) pauses while the filter sheet is open so arrow presses
            never swipe a card hidden behind the sheet. */}
        <SwipeDeck filters={filters} pauseKeys={sheetOpen} />
      </main>

      <FilterSheet open={sheetOpen} onClose={() => setSheetOpen(false)} filters={filters} onApply={setFilters} />
    </div>
  );
}
